/* eslint-disable max-lines-per-function */
import Spinner from "frontend/src/components/Spinner";
import { InvestmentTile } from "Components/InvestmentTile";
import Models from "Models/index.js";

import React, { forwardRef, lazy, Suspense, useRef, useMemo } from "react";
import Tooltip from "frontend/src/components/Tooltip/Tooltip";

// Both of these pull in Highcharts (~415KB). This tooltip is mounted from the
// app shell but its body only renders on hover, so loading them eagerly put
// Highcharts on the critical path for every page load.
const PerformancePrivateGraph = lazy(
  () => import("Components/Performance/PrivatePerformance/Graph")
);
const BenchmarktTimeSeries = lazy(
  () => import("Components/show/analytics/components/BenchmarkTimeSeriesChart")
);

interface Fund {
  id: number;
  name?: string | null;
  badgeColor?: string;
  market?: {
    __typename?: "AssetClassEnum" | undefined;
    id: number;
    name: string;
  } | null;
  hasIRRPerformance?: boolean | null;
  hasMTDPerformance?: boolean | null;
}

interface Alternative {
  id: string;
  name: string;
}

interface InvestmentPreviewTooltipProps {
  fund: Fund;
  alternatives?: Alternative[];
  children: React.ReactElement;
}

const InvestmentPreviewTooltip = forwardRef<
  HTMLDivElement,
  InvestmentPreviewTooltipProps
>(({ alternatives = [], fund, children }, ref) => {
  const tooltipArrow = useRef<HTMLDivElement>(null);

  const toAlternative = (alternative: Alternative): JSX.Element => (
    <li key={alternative.id}>{alternative.name}</li>
  );

  const overlay = useMemo((): React.ReactNode => {
    const { badgeColor } = fund || new Models.Fund();

    if (fund && fund.name) {
      return (
        <div className={`fund-tooltip fund-tooltip--${badgeColor}`}>
          <div className="fund-tooltip__col fund-tooltip__col--left">
            <InvestmentTile id={fund.id} />
          </div>

          <div className="fund-tooltip__col fund-tooltip__col--right">
            {(fund.hasIRRPerformance || fund.hasMTDPerformance) && (
              <div className={`fund-tooltip__chart ${fund.badgeColor}`}>
                <Suspense fallback={<Spinner />}>
                  {fund.market?.id === 1 &&
                    (fund.hasMTDPerformance ? (
                      <BenchmarktTimeSeries
                        investmentId={fund.id}
                        isThumbnail
                        type="growthOf1000"
                      />
                    ) : (
                      <PerformancePrivateGraph id={fund.id} />
                    ))}
                  {fund.market?.id === 2 &&
                    (fund.hasIRRPerformance ? (
                      <PerformancePrivateGraph id={fund.id} />
                    ) : (
                      <BenchmarktTimeSeries
                        investmentId={fund.id}
                        isThumbnail
                        type="growthOf1000"
                      />
                    ))}
                </Suspense>
              </div>
            )}

            <p className="fund-tooltip__offer-title">You May Also Like</p>
            <ul className="fund-tooltip__offer-list">
              {alternatives.map(toAlternative)}
            </ul>
          </div>
        </div>
      );
    }
    return (
      <div className="fund-tooltip fund-tooltip--loading">
        <Spinner />
      </div>
    );
  }, [fund, alternatives]);

  return (
    <div ref={ref}>
      <Tooltip
        trigger={["hover"]}
        placement="left-start"
        mouseLeaveDelay={0.1}
        mouseEnterDelay={0.25}
        title={overlay}
        overlayClassName="fund-tooltip-wrap rc-tooltip"
        destroyTooltipOnHide={true}
        arrow={{
          pointAtCenter: false,
          arrowPointAtCenter: false,
        }}
      >
        {children}
      </Tooltip>
    </div>
  );
});

InvestmentPreviewTooltip.displayName = "InvestmentPreviewTooltip";

export default InvestmentPreviewTooltip;
